// src/components/Admin/SignalsCuration.js
import React, { useState, useEffect, useCallback } from 'react';

const SignalsCuration = () => {
  const [signalsContent, setSignalsContent] = useState([]);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [activeTab, setActiveTab] = useState('pending'); // 'pending', 'active', 'scheduled', 'expired'
  const [sortBy, setSortBy] = useState('newest');
  const [brandFilter, setBrandFilter] = useState('');
  const [campaignFilter, setCampaignFilter] = useState('');
  const [previewItem, setPreviewItem] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);

  const [newSignal, setNewSignal] = useState({
    brand: '',
    title: '',
    description: '',
    imageUrl: '',
    sourceUrl: '',
    affiliateUrl: '',
    callToAction: 'View',
    category: 'lifestyle',
    tags: '',
    signals: '',
    featured: false,
    priority: 1,
    startDate: '',
    endDate: '',
    status: 'pending'
  });

  const categories = ['lifestyle', 'design', 'architecture', 'art', 'fashion', 'travel', 'technology'];
  const brands = ['Hermes', 'Prada', 'Valextra', 'Smythson', 'Arc\'teryx', 'Mystery Ranch', 'Oliver Peoples', 'James Perse', 'Barbour', 'Frette', 'Future Perfect', 'Galerie Half', 'Rewire Lighting', 'Gohar World', 'Arclinea', 'Niwaki', 'Aman', 'Snow Peak', 'Helinox', 'Pelican'];

  // Mock signals data - in reality would come from Firebase/database
  const mockSignals = [
    {
      id: 'signal_001',
      brand: 'Hermes',
      title: 'Timeless Craftsmanship',
      description: 'Where heritage meets contemporary design in leather goods that transcend seasons.',
      imageUrl: '/api/placeholder/600/800',
      sourceUrl: 'https://hermes.com/collection',
      affiliateUrl: 'https://hermes.com/collection?ref=artifex',
      callToAction: 'Explore Collection',
      category: 'lifestyle',
      tags: ['luxury', 'leather', 'craftsmanship'],
      signals: ['premium', 'timeless', 'artisanal', 'heritage'],
      featured: true,
      priority: 5,
      startDate: Date.now() + 86400000, // Tomorrow
      endDate: Date.now() + 2592000000, // 30 days from now
      status: 'pending',
      campaign: 'Spring 2025',
      createdAt: Date.now() - 86400000, // 1 day ago
      submittedBy: 'Brand Partner',
      estimatedReach: 2500,
      proposedBudget: 5000
    },
    {
      id: 'signal_002',
      brand: 'Snow Peak',
      title: 'Elevated Outdoor Living',
      description: 'Japanese design philosophy meets functional outdoor equipment for mindful adventures.',
      imageUrl: '/api/placeholder/600/800',
      sourceUrl: 'https://snowpeak.com/collections/camping',
      affiliateUrl: 'https://snowpeak.com/collections/camping?ref=artifex',
      callToAction: 'Shop Now',
      category: 'design',
      tags: ['outdoor', 'japanese', 'minimalist'],
      signals: ['functional', 'minimalist', 'outdoor', 'japanese aesthetic'],
      featured: false,
      priority: 3,
      startDate: Date.now() - 86400000, // Yesterday
      endDate: Date.now() + 1296000000, // 15 days from now
      status: 'active',
      campaign: 'Winter Gear 2025',
      createdAt: Date.now() - 259200000, // 3 days ago
      submittedBy: 'Direct Partnership',
      estimatedReach: 1800,
      actualClicks: 324,
      actualImpressions: 1650
    },
    {
      id: 'signal_003',
      brand: 'Future Perfect',
      title: 'Contemporary Design Icons',
      description: 'Curated furniture and objects that define the future of interior design.',
      imageUrl: '/api/placeholder/600/800',
      sourceUrl: 'https://thefutureperfect.com/new-arrivals',
      affiliateUrl: 'https://thefutureperfect.com/new-arrivals?ref=artifex',
      callToAction: 'Discover',
      category: 'design',
      tags: ['furniture', 'contemporary', 'curated'],
      signals: ['modern', 'sculptural', 'statement', 'gallery'],
      featured: true,
      priority: 4,
      startDate: Date.now() + 172800000, // 2 days from now
      endDate: Date.now() + 4320000000, // 50 days from now
      status: 'scheduled',
      campaign: 'New Arrivals Q1',
      createdAt: Date.now() - 172800000, // 2 days ago
      submittedBy: 'Affiliate Program',
      estimatedReach: 3200,
      proposedBudget: 8000
    }
  ];

  // Load signals data
  const loadSignalsData = useCallback(async () => {
    setIsLoading(true);
    try {
      // In reality, this would fetch from your backend/Firebase
      await new Promise(resolve => setTimeout(resolve, 500)); // Simulate API call
      setSignalsContent(mockSignals);
      setMessage(`✅ Loaded ${mockSignals.length} signals`);
    } catch (error) {
      console.error('Error loading signals:', error);
      setMessage('❌ Error loading signals data');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSignalsData();
  }, [loadSignalsData]);

  // Filter and sort signals
  const getFilteredSignals = useCallback(() => {
    let filtered = [...signalsContent];
    
    // Filter by tab
    switch (activeTab) {
      case 'pending':
        filtered = filtered.filter(item => item.status === 'pending');
        break;
      case 'active':
        filtered = filtered.filter(item => item.status === 'active');
        break;
      case 'scheduled':
        filtered = filtered.filter(item => item.status === 'scheduled');
        break;
      case 'expired':
        filtered = filtered.filter(item => item.status === 'expired' || 
          (item.endDate && new Date(item.endDate) < new Date()));
        break;
    }
    
    // Apply brand filter
    if (brandFilter) {
      filtered = filtered.filter(item => item.brand === brandFilter);
    }
    
    // Apply campaign filter
    if (campaignFilter) {
      filtered = filtered.filter(item => item.campaign === campaignFilter);
    }
    
    // Apply sorting
    const sorted = filtered.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return (b.createdAt || 0) - (a.createdAt || 0);
        case 'oldest':
          return (a.createdAt || 0) - (b.createdAt || 0);
        case 'priority':
          return (b.priority || 0) - (a.priority || 0);
        case 'brand':
          return (a.brand || '').localeCompare(b.brand || '');
        case 'startDate':
          return (a.startDate || 0) - (b.startDate || 0);
        case 'performance':
          return (b.actualClicks || 0) - (a.actualClicks || 0);
        default:
          return 0;
      }
    });
    
    return sorted;
  }, [signalsContent, activeTab, brandFilter, campaignFilter, sortBy]);

  const filteredSignals = getFilteredSignals();

  // Handle signal actions
  const handleApproveItems = async (itemIds) => {
    if (itemIds.length === 0) {
      setMessage('❌ No items selected');
      return;
    }

    if (!window.confirm(`Approve ${itemIds.length} signals and make them live?`)) {
      return;
    }

    setIsLoading(true);
    try {
      console.log('Approving signals:', itemIds);
      setMessage(`✅ Would approve ${itemIds.length} signals (backend API needed)`);
      setSelectedItems(new Set());
    } catch (error) {
      console.error('Error approving signals:', error);
      setMessage('❌ Error approving signals');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRejectItems = async (itemIds) => {
    if (itemIds.length === 0) {
      setMessage('❌ No items selected');
      return;
    }

    if (!window.confirm(`Reject ${itemIds.length} signals? This cannot be undone.`)) {
      return;
    }

    setIsLoading(true);
    try {
      console.log('Rejecting signals:', itemIds);
      setMessage(`✅ Would reject ${itemIds.length} signals (backend API needed)`);
      setSelectedItems(new Set());
    } catch (error) {
      console.error('Error rejecting signals:', error);
      setMessage('❌ Error rejecting signals');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePauseItems = async (itemIds) => {
    if (itemIds.length === 0) {
      setMessage('❌ No items selected');
      return;
    }

    setIsLoading(true);
    try {
      console.log('Pausing signals:', itemIds);
      setMessage(`✅ Would pause ${itemIds.length} signals (backend API needed)`);
      setSelectedItems(new Set());
    } catch (error) {
      console.error('Error pausing signals:', error);
      setMessage('❌ Error pausing signals');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle item selection
  const handleItemSelect = (itemId) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedItems(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedItems.size === filteredSignals.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(filteredSignals.map(item => item.id)));
    }
  };

  // Handle new signal form
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewSignal(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmitNewSignal = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const signal = {
        ...newSignal,
        id: `signal_${Date.now()}`,
        createdAt: Date.now(),
        tags: newSignal.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
        signals: newSignal.signals.split(',').map(signal => signal.trim()).filter(signal => signal)
      };
      
      console.log('Creating new signal:', signal);
      setMessage('✅ Signal created successfully (backend API needed)');
      setShowCreateForm(false);
      setNewSignal({
        brand: '',
        title: '',
        description: '',
        imageUrl: '',
        sourceUrl: '',
        affiliateUrl: '',
        callToAction: 'View',
        category: 'lifestyle',
        tags: '',
        signals: '',
        featured: false,
        priority: 1,
        startDate: '',
        endDate: '',
        status: 'pending'
      });
      
    } catch (error) {
      console.error('Error creating signal:', error);
      setMessage('❌ Error creating signal');
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return '#ffc107';
      case 'active': return '#28a745';
      case 'scheduled': return '#17a2b8';
      case 'expired': return '#6c757d';
      case 'rejected': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return '⏳';
      case 'active': return '🟢';
      case 'scheduled': return '📅';
      case 'expired': return '⏹️';
      case 'rejected': return '❌';
      default: return '❓';
    }
  };

  const getPriorityLabel = (priority) => {
    switch (priority) {
      case 5: return 'URGENT';
      case 4: return 'HIGH';
      case 3: return 'MEDIUM';
      case 2: return 'LOW';
      case 1: return 'MINIMAL';
      default: return 'NORMAL';
    }
  };

  return (
    <div style={{
      maxWidth: '1400px',
      margin: '2rem auto',
      padding: '2rem',
      backgroundColor: 'white',
      borderRadius: '12px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
    }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <div>
          <h2 style={{ margin: 0, color: '#333' }}>Signals Curation</h2>
          <p style={{ margin: '0.5rem 0 0', color: '#666', fontSize: '0.9rem' }}>
            Manage brand partnerships and signal placements
          </p>
        </div>
        
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <button
            onClick={() => window.location.href = '/admin'}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            ← Admin Dashboard
          </button>
          
          <button
            onClick={() => setShowCreateForm(!showCreateForm)}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: showCreateForm ? '#dc3545' : '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            {showCreateForm ? '❌ Cancel' : '📡 New Signal'}
          </button>
          
          <button
            onClick={loadSignalsData}
            disabled={isLoading}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#17a2b8',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Create Signal Form */}
      {showCreateForm && (
        <div style={{
          marginBottom: '2rem',
          padding: '2rem',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          border: '1px solid #dee2e6'
        }}>
          <h3 style={{ margin: '0 0 1.5rem 0', color: '#333' }}>Create New Signal</h3>
          
          <form onSubmit={handleSubmitNewSignal}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                  Brand *
                </label>
                <select
                  name="brand"
                  value={newSignal.brand}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '1rem'
                  }}
                >
                  <option value="">Select Brand</option>
                  {brands.map(brand => (
                    <option key={brand} value={brand}>{brand}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                  Editorial Title *
                </label>
                <input
                  type="text"
                  name="title"
                  value={newSignal.title}
                  onChange={handleInputChange}
                  required
                  placeholder="Poetic, minimal title (3-7 words)"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '1rem'
                  }}
                />
              </div>
            </div>
            
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                Description *
              </label>
              <textarea
                name="description"
                value={newSignal.description}
                onChange={handleInputChange}
                required
                placeholder="1-2 sophisticated sentences focusing on design qualities"
                rows="2"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '1rem',
                  resize: 'vertical'
                }}
              />
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                  Image URL *
                </label>
                <input
                  type="url"
                  name="imageUrl"
                  value={newSignal.imageUrl}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '1rem'
                  }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                  Source URL *
                </label>
                <input
                  type="url"
                  name="sourceUrl"
                  value={newSignal.sourceUrl}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '1rem'
                  }}
                />
              </div>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                  Affiliate URL
                </label>
                <input
                  type="url"
                  name="affiliateUrl"
                  value={newSignal.affiliateUrl}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '1rem'
                  }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                  Call to Action
                </label>
                <input
                  type="text"
                  name="callToAction"
                  value={newSignal.callToAction}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '1rem'
                  }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                  Priority (1-5)
                </label>
                <select
                  name="priority"
                  value={newSignal.priority}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '1rem'
                  }}
                >
                  <option value={1}>1 - Minimal</option>
                  <option value={2}>2 - Low</option>
                  <option value={3}>3 - Medium</option>
                  <option value={4}>4 - High</option>
                  <option value={5}>5 - Urgent</option>
                </select>
              </div>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                  Category
                </label>
                <select
                  name="category"
                  value={newSignal.category}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '1rem'
                  }}
                >
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                  Start Date
                </label>
                <input
                  type="datetime-local"
                  name="startDate"
                  value={newSignal.startDate}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '1rem'
                  }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                  End Date
                </label>
                <input
                  type="datetime-local"
                  name="endDate"
                  value={newSignal.endDate}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '1rem'
                  }}
                />
              </div>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                  Tags (comma-separated)
                </label>
                <input
                  type="text"
                  name="tags"
                  value={newSignal.tags}
                  onChange={handleInputChange}
                  placeholder="e.g. luxury, leather, craftsmanship"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '1rem'
                  }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                  Signals (comma-separated)
                </label>
                <input
                  type="text"
                  name="signals"
                  value={newSignal.signals}
                  onChange={handleInputChange}
                  placeholder="e.g. premium, timeless, artisanal"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '1rem'
                  }}
                />
              </div>
            </div>
            
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="checkbox"
                  name="featured"
                  checked={newSignal.featured}
                  onChange={handleInputChange}
                />
                <span style={{ fontWeight: '600' }}>Featured Signal (premium placement)</span>
              </label>
            </div>
            
            <button
              type="submit"
              disabled={isLoading}
              style={{
                padding: '0.75rem 2rem',
                backgroundColor: isLoading ? '#ccc' : '#0d47a1',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: isLoading ? 'not-allowed' : 'pointer'
              }}
            >
              {isLoading ? 'Creating...' : 'Create Signal'}
            </button>
          </form>
        </div>
      )}

      {/* Tabs */}
      <div style={{ 
        display: 'flex', 
        borderBottom: '2px solid #e9ecef',
        marginBottom: '2rem'
      }}>
        {[
          { id: 'pending', label: 'Pending Approval', count: signalsContent.filter(s => s.status === 'pending').length },
          { id: 'active', label: 'Live Signals', count: signalsContent.filter(s => s.status === 'active').length },
          { id: 'scheduled', label: 'Scheduled', count: signalsContent.filter(s => s.status === 'scheduled').length },
          { id: 'expired', label: 'Expired', count: signalsContent.filter(s => s.status === 'expired').length }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '1rem 2rem',
              backgroundColor: 'transparent',
              border: 'none',
              borderBottom: activeTab === tab.id ? '2px solid #007bff' : '2px solid transparent',
              color: activeTab === tab.id ? '#007bff' : '#666',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: '600'
            }}
          >
            {tab.label} ({tab.count})
          </button>
        ))}
      </div>

      {/* Filters */}
      <div style={{ 
        display: 'flex', 
        gap: '1rem', 
        marginBottom: '1.5rem', 
        padding: '1rem', 
        backgroundColor: '#f8f9fa', 
        borderRadius: '8px',
        flexWrap: 'wrap'
      }}>
        <div style={{ flex: 1, minWidth: '150px' }}>
          <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem', fontWeight: '600' }}>
            Brand Filter
          </label>
          <select
            value={brandFilter}
            onChange={(e) => setBrandFilter(e.target.value)}
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '0.9rem'
            }}
          >
            <option value="">All Brands</option>
            {brands.map(brand => (
              <option key={brand} value={brand}>{brand}</option>
            ))}
          </select>
        </div>
        
        <div style={{ flex: 1, minWidth: '150px' }}>
          <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem', fontWeight: '600' }}>
            Campaign Filter
          </label>
          <select
            value={campaignFilter}
            onChange={(e) => setCampaignFilter(e.target.value)}
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '0.9rem'
            }}
          >
            <option value="">All Campaigns</option>
            <option value="Spring 2025">Spring 2025</option>
            <option value="Winter Gear 2025">Winter Gear 2025</option>
            <option value="New Arrivals Q1">New Arrivals Q1</option>
          </select>
        </div>
        
        <div style={{ flex: 1, minWidth: '150px' }}>
          <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem', fontWeight: '600' }}>
            Sort By
          </label>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '0.9rem'
            }}
          >
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
            <option value="priority">Priority</option>
            <option value="brand">Brand A-Z</option>
            <option value="startDate">Start Date</option>
            {activeTab === 'active' && <option value="performance">Performance</option>}
          </select>
        </div>
      </div>

      {/* Bulk Actions - show for pending and active tabs */}
      {(activeTab === 'pending' || activeTab === 'active') && (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '1rem',
          padding: '1rem',
          backgroundColor: '#e9ecef',
          borderRadius: '6px'
        }}>
          <div>
            <button
              onClick={handleSelectAll}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.9rem',
                marginRight: '1rem'
              }}
            >
              {selectedItems.size === filteredSignals.length ? 'Deselect All' : 'Select All'}
            </button>
            
            <span style={{ fontSize: '0.9rem', color: '#666' }}>
              {selectedItems.size} of {filteredSignals.length} selected
            </span>
          </div>
          
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {activeTab === 'pending' && (
              <>
                <button
                  onClick={() => handleApproveItems(Array.from(selectedItems))}
                  disabled={selectedItems.size === 0 || isLoading}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: selectedItems.size > 0 ? '#28a745' : '#ccc',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: selectedItems.size > 0 ? 'pointer' : 'not-allowed',
                    fontSize: '0.9rem'
                  }}
                >
                  🟢 Approve Selected ({selectedItems.size})
                </button>
                
                <button
                  onClick={() => handleRejectItems(Array.from(selectedItems))}
                  disabled={selectedItems.size === 0 || isLoading}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: selectedItems.size > 0 ? '#dc3545' : '#ccc',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: selectedItems.size > 0 ? 'pointer' : 'not-allowed',
                    fontSize: '0.9rem'
                  }}
                >
                  ❌ Reject Selected ({selectedItems.size})
                </button>
              </>
            )}
            
            {activeTab === 'active' && (
              <button
                onClick={() => handlePauseItems(Array.from(selectedItems))}
                disabled={selectedItems.size === 0 || isLoading}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: selectedItems.size > 0 ? '#ffc107' : '#ccc',
                  color: selectedItems.size > 0 ? '#000' : 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: selectedItems.size > 0 ? 'pointer' : 'not-allowed',
                  fontSize: '0.9rem'
                }}
              >
                ⏸️ Pause Selected ({selectedItems.size})
              </button>
            )}
          </div>
        </div>
      )}

      {/* Signals Grid */}
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '2rem', fontSize: '1.1rem', color: '#666' }}>
          Loading signals...
        </div>
      ) : (
        <div>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))', 
            gap: '1.5rem' 
          }}>
            {filteredSignals.map((signal) => (
              <div key={signal.id} style={{
                border: '1px solid #ddd',
                borderRadius: '8px',
                padding: '1.5rem',
                backgroundColor: 'white',
                position: 'relative',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}>
                {/* Selection checkbox for pending and active */}
                {(activeTab === 'pending' || activeTab === 'active') && (
                  <input
                    type="checkbox"
                    checked={selectedItems.has(signal.id)}
                    onChange={() => handleItemSelect(signal.id)}
                    style={{
                      position: 'absolute',
                      top: '1rem',
                      left: '1rem',
                      transform: 'scale(1.2)',
                      zIndex: 1
                    }}
                  />
                )}

                {/* Status and Priority indicators */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', marginTop: (activeTab === 'pending' || activeTab === 'active') ? '2rem' : '0' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ fontSize: '1.2rem' }}>
                      {getStatusIcon(signal.status)}
                    </span>
                    <span style={{
                      backgroundColor: getStatusColor(signal.status),
                      color: 'white',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '12px',
                      fontSize: '0.75rem',
                      fontWeight: 'bold'
                    }}>
                      {signal.status.toUpperCase()}
                    </span>
                  </div>
                  
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    {signal.featured && (
                      <span style={{
                        backgroundColor: '#ffc107',
                        color: '#000',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '12px',
                        fontSize: '0.7rem',
                        fontWeight: 'bold'
                      }}>
                        ⭐ FEATURED
                      </span>
                    )}
                    
                    <span style={{
                      backgroundColor: signal.priority >= 4 ? '#dc3545' : signal.priority >= 3 ? '#ffc107' : '#6c757d',
                      color: signal.priority >= 3 ? (signal.priority >= 4 ? 'white' : '#000') : 'white',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '12px',
                      fontSize: '0.7rem',
                      fontWeight: 'bold'
                    }}>
                      {getPriorityLabel(signal.priority)}
                    </span>
                  </div>
                </div>

                {/* Signal preview image */}
                <div style={{ marginBottom: '1rem' }}>
                  <img 
                    src={signal.imageUrl} 
                    alt={signal.title}
                    style={{
                      width: '100%',
                      height: '200px',
                      objectFit: 'cover',
                      borderRadius: '6px',
                      backgroundColor: '#f8f9fa'
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
                
                {/* Signal content */}
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <h3 style={{ 
                      margin: 0, 
                      fontSize: '1.1rem', 
                      fontWeight: '600',
                      color: '#333'
                    }}>
                      {signal.brand}
                    </h3>
                    
                    <button
                      onClick={() => setPreviewItem(signal)}
                      style={{
                        padding: '0.25rem 0.5rem',
                        backgroundColor: '#17a2b8',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.8rem'
                      }}
                    >
                      👁️ Preview
                    </button>
                  </div>
                  
                  <h4 style={{ 
                    margin: '0 0 0.5rem 0', 
                    fontSize: '1rem', 
                    fontWeight: '500',
                    color: '#555',
                    fontStyle: 'italic'
                  }}>
                    "{signal.title}"
                  </h4>
                  
                  <p style={{ 
                    margin: '0 0 0.75rem 0', 
                    fontSize: '0.9rem', 
                    color: '#666',
                    lineHeight: '1.4'
                  }}>
                    {signal.description}
                  </p>
                  
                  <div style={{ fontSize: '0.8rem', color: '#888', marginBottom: '0.5rem' }}>
                    <strong>Campaign:</strong> {signal.campaign || 'No campaign'}
                  </div>
                  
                  <div style={{ fontSize: '0.8rem', color: '#888', marginBottom: '0.5rem' }}>
                    <strong>Category:</strong> {signal.category} • <strong>CTA:</strong> {signal.callToAction}
                  </div>
                  
                  {signal.tags && signal.tags.length > 0 && (
                    <div style={{ fontSize: '0.75rem', color: '#999', marginBottom: '0.5rem' }}>
                      <strong>Tags:</strong> {signal.tags.slice(0, 5).join(', ')}
                      {signal.tags.length > 5 && ' ...'}
                    </div>
                  )}
                  
                  {signal.signals && signal.signals.length > 0 && (
                    <div style={{ fontSize: '0.75rem', color: '#999', marginBottom: '0.5rem' }}>
                      <strong>Signals:</strong> {signal.signals.slice(0, 5).join(', ')}
                      {signal.signals.length > 5 && ' ...'}
                    </div>
                  )}
                  
                  {/* Performance metrics for active signals */}
                  {activeTab === 'active' && signal.actualClicks !== undefined && (
                    <div style={{ 
                      marginTop: '0.75rem',
                      padding: '0.5rem',
                      backgroundColor: '#e8f5e8',
                      borderRadius: '4px',
                      fontSize: '0.8rem'
                    }}>
                      <strong>Performance:</strong> {signal.actualClicks} clicks • {signal.actualImpressions} impressions
                      <div style={{ color: '#666', fontSize: '0.75rem' }}>
                        CTR: {((signal.actualClicks / signal.actualImpressions) * 100).toFixed(2)}%
                      </div>
                    </div>
                  )}
                  
                  {/* Campaign info for pending/scheduled */}
                  {(activeTab === 'pending' || activeTab === 'scheduled') && (
                    <div style={{ 
                      marginTop: '0.75rem',
                      padding: '0.5rem',
                      backgroundColor: '#f0f8ff',
                      borderRadius: '4px',
                      fontSize: '0.8rem'
                    }}>
                      {signal.startDate && (
                        <div><strong>Start:</strong> {new Date(signal.startDate).toLocaleDateString()}</div>
                      )}
                      {signal.endDate && (
                        <div><strong>End:</strong> {new Date(signal.endDate).toLocaleDateString()}</div>
                      )}
                      {signal.estimatedReach && (
                        <div><strong>Est. Reach:</strong> {signal.estimatedReach} users</div>
                      )}
                      {signal.proposedBudget && (
                        <div><strong>Budget:</strong> ${signal.proposedBudget}</div>
                      )}
                    </div>
                  )}
                  
                  <div style={{ fontSize: '0.7rem', color: '#aaa', marginTop: '0.75rem' }}>
                    Created: {new Date(signal.createdAt).toLocaleDateString()} • By: {signal.submittedBy}
                  </div>
                </div>
                
                {/* Action buttons */}
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  {signal.sourceUrl && (
                    <button
                      onClick={() => window.open(signal.sourceUrl, '_blank')}
                      style={{
                        flex: 1,
                        padding: '0.5rem',
                        backgroundColor: '#17a2b8',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.8rem'
                      }}
                    >
                      🔗 View Source
                    </button>
                  )}
                  
                  {activeTab === 'pending' && (
                    <>
                      <button
                        onClick={() => handleApproveItems([signal.id])}
                        disabled={isLoading}
                        style={{
                          flex: 1,
                          padding: '0.5rem',
                          backgroundColor: '#28a745',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '0.8rem'
                        }}
                      >
                        🟢 Approve
                      </button>
                      
                      <button
                        onClick={() => handleRejectItems([signal.id])}
                        disabled={isLoading}
                        style={{
                          padding: '0.5rem',
                          backgroundColor: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '0.8rem'
                        }}
                      >
                        ❌
                      </button>
                    </>
                  )}
                  
                  {activeTab === 'active' && (
                    <button
                      onClick={() => handlePauseItems([signal.id])}
                      disabled={isLoading}
                      style={{
                        padding: '0.5rem',
                        backgroundColor: '#ffc107',
                        color: '#000',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.8rem'
                      }}
                    >
                      ⏸️ Pause
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
          
          {filteredSignals.length === 0 && (
            <div style={{ 
              textAlign: 'center', 
              padding: '3rem', 
              color: '#666',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px'
            }}>
              <h3 style={{ margin: '0 0 0.5rem 0' }}>
                {activeTab === 'pending' && 'No pending signals'}
                {activeTab === 'active' && 'No active signals'}
                {activeTab === 'scheduled' && 'No scheduled signals'}
                {activeTab === 'expired' && 'No expired signals'}
              </h3>
              <p style={{ margin: 0 }}>
                {activeTab === 'pending' 
                  ? 'Create new signals or check your filters' 
                  : `No ${activeTab} signals yet`}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Signal Preview Modal */}
      {previewItem && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '2rem',
            maxWidth: '600px',
            maxHeight: '54.4vh',
            overflow: 'auto',
            position: 'relative'
          }}>
            <button
              onClick={() => setPreviewItem(null)}
              style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                backgroundColor: 'transparent',
                border: 'none',
                fontSize: '1.5rem',
                cursor: 'pointer',
                color: '#666'
              }}
            >
              ✕
            </button>
            
            <h3 style={{ margin: '0 0 1rem 0', color: '#333' }}>Signal Preview</h3>
            <p style={{ margin: '0 0 1.5rem 0', color: '#666', fontSize: '0.9rem' }}>
              This is how the signal will appear to users in the Signals feed:
            </p>
            
            {/* Preview of signal as it would appear in actual feed */}
            <div style={{
              border: '1px solid #ddd',
              borderRadius: '8px',
              overflow: 'hidden',
              position: 'relative',
              backgroundColor: '#fff'
            }}>
              <img 
                src={previewItem.imageUrl} 
                alt={previewItem.title}
                style={{
                  width: '100%',
                  height: '400px',
                  objectFit: 'cover'
                }}
              />
              
              {/* Signal overlay as it would appear on hover */}
              <div style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
                color: 'white',
                padding: '2rem 1.5rem 1.5rem',
                opacity: 1
              }}>
                <div style={{ fontSize: '0.9rem', marginBottom: '0.5rem', opacity: 0.9 }}>
                  {previewItem.brand}
                </div>
                <h3 style={{ margin: '0 0 0.75rem 0', fontSize: '1.3rem', fontWeight: '600' }}>
                  {previewItem.title}
                </h3>
                <p style={{ margin: '0 0 1rem 0', fontSize: '0.95rem', lineHeight: '1.4', opacity: 0.95 }}>
                  {previewItem.description}
                </p>
                <button style={{
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  border: '1px solid rgba(255,255,255,0.3)',
                  padding: '0.5rem 1rem',
                  borderRadius: '4px',
                  fontSize: '0.9rem',
                  cursor: 'pointer'
                }}>
                  {previewItem.callToAction}
                </button>
              </div>
            </div>
            
            <div style={{ marginTop: '1rem', fontSize: '0.85rem', color: '#666' }}>
              <strong>Note:</strong> Users will see this signal in their personalized feed based on their Digital Card preferences.
            </div>
          </div>
        </div>
      )}

      {/* Status Message */}
      {message && (
        <div style={{
          marginTop: '1rem',
          padding: '0.75rem',
          backgroundColor: message.includes('❌') ? '#fee' : '#efe',
          color: message.includes('❌') ? '#c33' : '#363',
          borderRadius: '6px',
          fontSize: '0.9rem'
        }}>
          {message}
        </div>
      )}

      {/* Note about backend API needed */}
      <div style={{
        marginTop: '2rem',
        padding: '1rem',
        backgroundColor: '#fff3cd',
        border: '1px solid #ffeaa7',
        borderRadius: '6px',
        fontSize: '0.85rem',
        color: '#856404'
      }}>
        <strong>Note:</strong> This interface shows the signals approval workflow. You'll need a backend API to actually manage signal status, scheduling, and performance tracking. The approve/reject/pause actions currently just log what would happen.
      </div>
    </div>
  );
};

export default SignalsCuration;